import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import dataHistoryService from "@plannel/services/data/data-history-service";
import PaginationContainer from "@plannel/components/PaginationContainer"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import Snackbar from "@plannel/components/Snackbar";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import KeyValueList from "../KeyValueList"
import ImportErrorDetail from "./ImportErrorDetail";
import { StatusChip, formatDuration, secondsBetween } from "./HistoryGridParts";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import { FS } from "@plannel/utils/font-util";
import ColorUtil from "../utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";

const { snackBarMessageColor } = ColorUtil;

/*
 * 상태 세그먼트 → 조회 파라미터. jobStatus 는 complete_flg, jobError 는 error_cnt 기준(Y=오류 있음, N=오류 없음).
 * 예전 화면은 두 축을 드롭다운 두 개로 따로 골랐다.
 */
const STATUS_FILTERS = {
  all: {},
  running: { jobStatus: "N" },
  complete: { jobStatus: "Y", jobError: "N" },
  withErrors: { jobError: "Y" },
};

// aborted 는 서버가 판정해 내려준다(끝나지 않은 채 1시간 넘은 작업 — DataLoadHistoryController.ABORTED_AFTER).
const getImportStatus = data => {
  if (data?.completeFlg !== "Y") {
    return data?.aborted ? "aborted" : "running";
  }
  return data?.errorCnt > 0 ? "withErrors" : "complete";
};

const STATUS_TONE = { running: "warning", complete: "success", withErrors: "error", aborted: "neutral" };

// 정렬은 서버로 colId 를 그대로 넘긴다(orderByColumn) → 화면 전용 colId 는 실제 필드로 바꿔 보낸다.
const SORT_FIELD_BY_COL_ID = { importStatus: "completeFlg", importResult: "errorCnt" };

const ImportStatusChip = ({ data }) => {
  const status = getImportStatus(data);
  return <StatusChip label={status} tone={STATUS_TONE[status]} />;
};

const ResultBar = ({ data }) => {
  const success = data?.successCnt ?? 0;
  const error = data?.errorCnt ?? 0;
  const total = success + error;
  const successPct = total > 0 ? Math.min(Math.max(success / total * 100, 0), 100) : 0;

  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%", gap: 1 }}>
      <Box sx={{ flex: 1, height: 8, bgcolor: "grey.200", borderRadius: 1, overflow: "hidden", display: "flex" }}>
        {total > 0 && <>
          <Box sx={{ width: `${successPct}%`, height: "100%", bgcolor: "success.main" }} />
          <Box sx={{ width: `${100 - successPct}%`, height: "100%", bgcolor: "error.main" }} />
        </>}
      </Box>
      <Box component="span" sx={{ minWidth: 104, textAlign: "right", fontSize: FS.CAPTION, fontVariantNumeric: "tabular-nums" }}>
        {success.toLocaleString()}
        {error > 0 && <> / <Box component="b" sx={{ color: "error.main", textDecoration: "underline", cursor: "pointer" }}>{error.toLocaleString()}</Box></>}
      </Box>
    </Box>
  );
};

/*
 * 펼친 오류 영역 높이 — 오류 수에 맞추고 8줄에서 멈춘다(그 이상은 안쪽 그리드가 스크롤).
 * 고정 높이로 두면 오류 1건에도 빈 칸이 300px 남는다.
 * 숫자는 ImportErrorDetail 레이아웃과 짝: 위아래 여백 24 + 칩 줄 32 + 간격 8 + 헤더 40(DefaultGridSetting) + 행 28(balham) + 테두리 2.
 */
const DETAIL_MAX_ROWS = 8;
const getDetailRowHeight = ({ node, data }) => {
  if (!node.detail) {
    return undefined;
  }
  const rows = Math.min(Math.max(data?.errHistory?.length ?? 0, 1), DETAIL_MAX_ROWS);
  return 24 + 32 + 8 + 40 + rows * 28 + 2;
};

const durationGetter = ({ data }) => secondsBetween(data?.startTs, data?.endTs);

const DataHistory = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.page ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [tPages, setTotalPages] = useState(0);
  const [jobTableForSelect, setJobTableForSelect] = useState(reduxViewState?.jobTableForSelect ?? []);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const jobModule = useRef(reduxViewState?.searchCode?.jobModule ?? "");
  const jobTable = useRef(reduxViewState?.searchCode?.jobTable ?? "");
  const [statusFilter, setStatusFilter] = useState(() => {
    const saved = reduxViewState?.searchCode?.statusFilter;
    return STATUS_FILTERS[saved] ? saved : "all";
  });
  const [sortParams, setSortParams] = useState({});
  const [dataHistories, setDataHistories] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const importOptions = KeyValueList.findByCodeGroupList("IMPORT_OPTION");
  const separatorOptions = KeyValueList.findByCodeGroupList("SEPARATOR_OPTION");

  const optionLabel = (mappings, value) => {
    const { codeName } = KeyValueList.getKeyValue(mappings, value);
    return GridUtils.gridValueL10N(codeName);
  }

  const loadOptnFormatter = ({ value, data }) => {
    const loadOptn = value === 3
      ? GridUtils.gridValueL10N(data?.jobModuleCd === "Truncate" ? "truncate" : "deleteAndAdd")
      : optionLabel(importOptions, value);
    const delimiter = optionLabel(separatorOptions, data?.delimiterOptn);
    return delimiter ? `${loadOptn} · ${delimiter}` : loadOptn;
  }

  const toggleErrorDetail = (e) => {
    const { api, rowIndex, node, data } = e;

    if (data?.errHistory?.length > 0) {
      api.getDisplayedRowAtIndex(rowIndex).setExpanded(!node.expanded);
    }
  }

  const detailEnabled = (e) => {
    return e.errHistory?.length > 0;
  }

  const columnDefs = [
    {
      headerName: "status", colId: "importStatus", field: "completeFlg", type: "nonEditableColumn", width: 150,
      cellRenderer: "agGroupCellRenderer", cellRendererParams: { innerRenderer: ImportStatusChip, suppressCount: true },
    },
    { headerName: "module", field: "jobModuleCd", type: "nonEditableColumn", width: 110 },
    { headerName: "jobTableName", field: "jobTableName", type: "nonEditableColumn", valueFormatter: params => t(params.value), width: 180 },
    { headerName: "loadOptn", field: "loadOptn", type: "nonEditableColumn", valueFormatter: loadOptnFormatter, width: 170 },
    {
      ...GridUtils.composedHeader(() => `${GridUtils.gridValueL10N("successCnt")} / ${GridUtils.gridValueL10N("errorCnt")}`),
      colId: "importResult", field: "errorCnt", type: "nonEditableColumn", width: 240,
      cellRenderer: ResultBar, onCellClicked: toggleErrorDetail,
    },
    { headerName: "startTs", field: "startTs", type: "nonEditableColumn", valueFormatter: dateUtils.formatDateTime, width: 150 },
    {
      headerName: "duration", colId: "duration", type: ["nonEditableColumn", "rightAligned"], width: 110, sortable: false,
      valueGetter: durationGetter, valueFormatter: formatDuration,
    },
    { headerName: "descTxt", field: "descTxt", type: "nonEditableColumn", flex: 1, minWidth: 200, tooltipField: "descTxt" },
  ];

  const detailCellRendererParams = useMemo(() => ({
    title,
    onEmpty: () => setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [title]);

  const filteredJobTableNames = useMemo(() => {
    const jobString = jobTable.current;

    if (!jobTableForSelect || isEmpty(jobTableForSelect) || !jobString) {
      return [];
    }

    const translatedMap = jobTableForSelect.reduce((acc, key) => {
      const translated = t(key);
      (acc[translated] ??= []).push(key);
      return acc;
    }, {});

    return Object.entries(translatedMap)
      .filter(([translated]) => translated.toLowerCase().includes(jobString.toLowerCase()))
      .flatMap(([, originalKeys]) => originalKeys);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobTableForSelect, jobTable.current]);

  const getRequestParams = () => {
    const params = { ...STATUS_FILTERS[statusFilter] };

    params.jobModule = jobModule.current;
    params.jobTable = filteredJobTableNames.join(",");

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    return { ...params, ...sortParams };
  };

  const retrieveDataHistories = () => {
    const params = getRequestParams();
    dataHistoryService.getAll(params)
      .then(response => {
        const { results, totalPages, jobTableNms } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setDataHistories(results);
        setTotalPages(totalPages);
        if (jobTableNms && jobTableForSelect.length === 0) {
          setJobTableForSelect(jobTableNms);
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const searchCodes = { jobModule: jobModule.current, jobTableForSelect, jobTable: jobTable.current, statusFilter };
        const reduxState = reduxUtil.createViewState(viewName, searchCodes, currentPage, pageSize);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  }

  const onGridReady = (params) => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true, alignOrderToDefs: true });
  };

  const onSortChanged = params => {
    const sortState = GridUtils.getSortState(params);
    const field = SORT_FIELD_BY_COL_ID[sortState.orderByColumn];
    setSortParams(field ? { ...sortState, orderByColumn: field } : sortState);
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveDataHistories();
    }
  }

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveDataHistories();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams, filteredJobTableNames]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  return (
    <>
      <FilterContainer hasIcons={false}>
        <TextFieldSearch sx={{ width: 190, minWidth: 100, mr: 1  }} label="module" searchCode={jobModule} retrieve={initPageToRetrieve} />
        <TextFieldSearch sx={{ mr: 1.5, width: 190, minWidth: 100  }} label="jobTableName" searchCode={jobTable} retrieve={initPageToRetrieve} />
        <SegmentedControl
          value={statusFilter}
          onChange={setStatusFilter}
          // "실행 중" 조회(complete_flg=N)에는 중단된 작업도 함께 나온다 → 라벨에 둘 다 적는다.
          options={Object.keys(STATUS_FILTERS).map(key => ({
            value: key, label: key === "running" ? `${t("running")} · ${t("aborted")}` : t(key)
          }))}
        />
      </FilterContainer>
      <Box id="data-history-grid" className="ag-theme-balham grid-area" mb={1} >
        <AgGridReact
          ref={gridRef}
          rowData={dataHistories}
          {...defaultGridMemo}
          masterDetail
          isRowMaster={detailEnabled}
          detailCellRenderer={ImportErrorDetail}
          detailCellRendererParams={detailCellRendererParams}
          getRowHeight={getDetailRowHeight}
          onGridReady={onGridReady}
          onSortChanged={onSortChanged}
          rowSelection={{
            mode: 'multiRow',
            checkboxes: false,
            headerCheckbox: false,
            enableClickSelection: true,
          }}
        />
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(DataHistory);
